import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import { debounce } from 'lodash';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BluePin, PlusIcon, Recents, WhiteTrash } from '../../../../../assets/images';
import { Loader, SearchBar } from '../../../../../components';
import {
  ClearSessionText,
  LastDays,
  NewSessionText,
  NoSessionFoundText,
  Pinnedtext,
  TodayText,
} from '../data/constants';
import { chatInitialStateProps, Session } from '../../pages/chat-screen/chat-screen.props';
import { ChatScreenActionTypes } from '../../../../../utils/reducer-types';
import ChatHistoryCard from './chat-history-card';
import makeToast from '../../../../../components/toaster';
import {
  ClearAllChatSessions,
  CreateNewChatSession,
  GetAllChatHistorySessions,
  GetAllPinnedChatSessions,
  GetSessionById,
  RenameChatSession,
} from '../../services/chat';
import { pickDefaultChatModalForProvider, type LlmPermissionItem } from '../../../../../services';
import { ChatModelContext, LlmSetupBannerContext } from '../../../../../utils/context';
import { DataItem } from '../../../../../utils/interface-types';

interface ChatHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  state: chatInitialStateProps; // Replace with actual type if available
  dispatch: (action: ChatScreenActionTypes) => void; // Replace with actual type if available
  setIsContentAvailable: (arg: boolean) => void;
  activeProviderId: number;
  setActiveProvider: (arg: number) => void;
  setActiveTab: (arg: number) => void;
  llmPermissions: LlmPermissionItem[];
}

dayjs.extend(isToday);

function ChatHistoryPanel({
  isOpen,
  onClose,
  state,
  dispatch,
  setIsContentAvailable,
  activeProviderId,
  setActiveProvider,
  setActiveTab,
  llmPermissions,
}: ChatHistoryPanelProps) {
  const sessionId = state?.activeSession || 0;
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const inputSessionNameRef = useRef<HTMLInputElement | null>(null);
  const editedSessionRef = useRef(state?.editedSession);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const { setChatModel } = useContext(ChatModelContext);
  const { showLlmSetupBanner } = useContext(LlmSetupBannerContext);
  const [, setParsedExistingChat] = useState<
    { data: object; session_id: string; provider_id: number }[]
  >([]);
  useEffect(() => {
    editedSessionRef.current = state.editedSession;
  }, [state.editedSession]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Don't close if clicking the dropdown trigger button or the dropdown menu itself
      if (
        target.closest('[data-dropdown-trigger="true"]') ||
        target.closest('[data-dropdown-menu="true"]')
      ) {
        return;
      }

      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) {
        dispatch({ type: 'SET_ACTIVE_SESSION', payload: null });
      }
      if (
        inputSessionNameRef.current &&
        !inputSessionNameRef.current.contains(event.target as Node)
      ) {
        if (editedSessionRef?.current?.name === '') {
          makeToast('error', 'Please enter a session name');
          return;
        }
        if (state.editedSession?.name === editedSessionRef?.current?.name) {
          dispatch({
            type: 'SET_EDIT_SESSION_INDEX',
            payload: null,
          });
          dispatch({
            type: 'SET_EDITED_SESSION',
            payload: null,
          });
          dispatch({
            type: 'SET_SESSION_HISTORY',
            payload: {
              search: '',
              limit: 10,
              offset: 0,
            },
          });
          return;
        }
        const name = editedSessionRef?.current?.name;
        if (name && name.length > 90) {
          makeToast('error', 'Session name cannot exceed 90 characters');
          return;
        }
        const id = editedSessionRef?.current?.id;
        const editName = editedSessionRef?.current?.name || '';
        const pinned = editedSessionRef?.current?.pinned || 0;
        RenameChatSession(id!, editName, pinned)
          .then((res) => {
            makeToast('success', res?.data?.message);
            dispatch({
              type: 'SET_SESSION_HISTORY',
              payload: {
                search: '',
                limit: 10,
                offset: 0,
              },
            });
            dispatch({
              type: 'TOGGLE_DATA_CHANGED',
            });
            dispatch({
              type: 'SET_EDIT_SESSION_INDEX',
              payload: null,
            });
            dispatch({
              type: 'SET_EDITED_SESSION',
              payload: null,
            });
          })
          .catch(() => {
            makeToast('error', 'Error Renaming Session');
          });

        dispatch({
          type: 'SET_EDIT_SESSION_INDEX',
          payload: null,
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchPinnedSessions = async () => {
      dispatch({ type: 'SET_PINNED_LOADING', payload: true });
      try {
        const res = await GetAllPinnedChatSessions(state.pinSessionHistory);
        const pinned = res?.data?.data?.sessions || [];
        dispatch({ type: 'SET_PINNED_SESSIONS', payload: pinned });
      } catch (error) {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const msg = err?.response?.data?.message || err?.message || 'Error Fetching Sessions!';
        makeToast('error', msg);
      } finally {
        dispatch({ type: 'SET_PINNED_LOADING', payload: false });
      }
    };
    if (isOpen) {
      fetchPinnedSessions();
    }
  }, [isOpen, state?.dataChanged, state.pinSessionHistory]);

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'SET_SESSIONS_LOADING', payload: true });
      try {
        const sessionsRes = await GetAllChatHistorySessions(state.sessionHistory);
        const allSessions = sessionsRes?.data?.data?.sessions || [];
        const totalSessions = sessionsRes?.data?.data?.total_sessions || 0;
        const today: Session[] = [];
        const last7Days: Session[] = [];
        allSessions.forEach((session: Session) => {
          const createdDate = dayjs(session.created_at);
          if (createdDate.isToday()) {
            today.push({ ...session, type: 'session' });
          } else {
            last7Days.push({ ...session, type: 'session' });
          }
        });
        dispatch({
          type: 'SET_TOTAL_SESSIONS',
          payload: totalSessions,
        });
        if (state?.sessionHistory?.offset === 0) {
          dispatch({ type: 'SET_TODAY_SESSIONS', payload: today });
          dispatch({ type: 'SET_LAST7DAYS_SESSIONS', payload: last7Days });
        } else {
          dispatch({
            type: 'SET_TODAY_SESSIONS',
            payload: [
              ...state.todaySessions,
              ...today.filter(
                (session) =>
                  !state.todaySessions.some((existingSession) => existingSession.id === session.id),
              ),
            ],
          });
          dispatch({
            type: 'SET_LAST7DAYS_SESSIONS',
            payload: [
              ...state.last7DaysSessions,
              ...last7Days.filter(
                (session) =>
                  !state.last7DaysSessions.some(
                    (existingSession) => existingSession.id === session.id,
                  ),
              ),
            ],
          });
        }
      } catch (error: unknown) {
        makeToast(
          'error',
          (error as { response: { data: { message: string } } })?.response?.data?.message ||
            'Error Fetching Chat Sessions!',
        );
      } finally {
        dispatch({ type: 'SET_SESSIONS_LOADING', payload: false });
      }
    };
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, state?.dataChanged, state.sessionHistory]);

  const handleNewSession = () => {
    const existingChat = JSON.parse(localStorage.getItem('chat') || '[]');
    const providerObj = existingChat.find(
      (item: { provider_id: number }) => item.provider_id === activeProviderId,
    );
    const modalId = providerObj?.data?.modal?.id;
    if (!modalId) {
      makeToast('error', 'No model selected for this provider!');
      return;
    }

    CreateNewChatSession({ model_id: modalId, provider_id: activeProviderId || 0 })
      .then((res) => {
        if (res.status === 201) {
          const updatedChat = existingChat?.map(
            (item: { data: { chatData: [] }; provider_id: number; session_id: string }) => {
              if (item.provider_id === activeProviderId) {
                return {
                  ...item,
                  session_id: res?.data?.data?.session_id,
                  data: {
                    ...item.data,
                    chatData: [], // Empty the chatData array
                    file_id: undefined, // Clear file_id
                    use_document: false, // Reset use_document to false
                    has_document: false, // Reset has_document to false
                    webSearchEnabled: false,
                    webSearchMode: 'auto',
                  },
                };
              }
              return {
                ...item,
                session_id: item.session_id ? item.session_id : null,
              };
            },
          );
          setParsedExistingChat(updatedChat);
          localStorage.setItem('chat', JSON.stringify(updatedChat));
          dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
          dispatch({ type: 'SET_WEB_SEARCH_MODE', payload: 'auto' });
          dispatch({ type: 'TOGGLE_DATA_CHANGED' });
          setIsContentAvailable(false);
          onClose();
        }
      })
      .catch((error) => {
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const msg = err?.response?.data?.message || err?.message || 'Something went wrong!';
        makeToast('error', msg);
      });
  };

  const initializeProviderSession = (providerId: number) => {
    let currentParsedData: {
      provider_id: number;
      data?: { chatData?: unknown[] };
      session_id?: string | null;
    }[] = [];

    try {
      currentParsedData = JSON.parse(localStorage.getItem('chat') || '[]');
    } catch (error) {
      makeToast('error', 'Error parsing chat data from localStorage');
      throw new Error(`Invalid chat data format ${error}`);
    }

    const providerExists = currentParsedData.some(
      (item: { provider_id: number }) => item.provider_id === providerId,
    );

    if (!providerExists) {
      const permissions = llmPermissions;
      const modal = pickDefaultChatModalForProvider(permissions, providerId);

      const newProviderData = {
        provider_id: providerId,
        session_id: null,
        data: {
          chatData: [],
          use_document: false,
          has_document: false,
          modal,
          file_id: '',
        },
      };

      currentParsedData = [...currentParsedData, newProviderData];
      localStorage.setItem('chat', JSON.stringify(currentParsedData));
    }

    const updatedTabData = currentParsedData.find(
      (entry: { provider_id: number }) => entry.provider_id === providerId,
    );

    if (!updatedTabData || updatedTabData.data?.chatData?.length === 0) {
      dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
      setIsContentAvailable(false);
    } else {
      dispatch({
        type: 'SET_CHAT_HISTORY',
        payload: updatedTabData.data?.chatData as DataItem[],
      });
      setIsContentAvailable(true);
    }

    dispatch({ type: 'SET_ACTIVE_PROVIDER', payload: providerId });
    dispatch({ type: 'SET_SHOW_EDIT_QUERY_ACTIONS', payload: false });
    dispatch({ type: 'SET_QUERY_VALUE', payload: '' });
  };

  const handleSessionId = (
    id: string | number,
    providerName: string,
    providerId: number | string,
  ) => {
    dispatch({ type: 'SET_CHAT_SCREEN_LOADING', payload: true });
    setActiveProvider(providerId as number);
    setActiveTab(providerId as number);
    localStorage.setItem('activeProvider', providerName);

    initializeProviderSession(providerId as number); // 🛠️ Immediate setup

    const history: string | null = localStorage.getItem('chat');
    const parsedHistory = history ? JSON.parse(history) : [];

    GetSessionById(id)
      .then((res) => {
        if (res.status === 200) {
          const sessionData = res?.data?.data;

          const updatedHistory = parsedHistory.map((item: { provider_id: number }) =>
            item.provider_id === providerId ? sessionData : item,
          );

          localStorage.setItem('chat', JSON.stringify(updatedHistory));

          const activeSession = updatedHistory.find(
            (item: { provider_id: number }) => item.provider_id === providerId,
          );

          if (activeSession?.data?.chatData?.length > 0) {
            setIsContentAvailable(true);
          } else {
            setIsContentAvailable(false);
          }

          let chatHistory = activeSession?.data?.chatData;

          if (Array.isArray(chatHistory)) {
            chatHistory = chatHistory.map((item) => ({ ...item }));
          } else if (chatHistory !== undefined && chatHistory !== null) {
            chatHistory = [chatHistory];
          } else {
            chatHistory = [];
          }
          setChatModel(`${activeSession?.data?.modal?.name}|${activeSession?.data?.modal?.id}`);
          dispatch({ type: 'SET_CHAT_HISTORY', payload: sessionData?.data?.chatData });
          dispatch({ type: 'SET_CHAT_SCREEN_LOADING', payload: false });
        }
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          makeToast('error', err?.response?.data?.message);
          return;
        }
        makeToast('error', 'Error Getting the Session!');
        dispatch({ type: 'SET_CHAT_SCREEN_LOADING', payload: false });
      });
  };

  const handleClearSessions = () => {
    ClearAllChatSessions()
      .then((res) => {
        if (res?.status === 200) {
          const history: string | null = localStorage.getItem(`chat`);
          const parsedHistory: {
            provider_id: number;
            session_id: string;
            data: { chatData: []; modal: { id: number } };
          }[] = history ? JSON.parse(history) : [];

          const updatedData = parsedHistory.map((item) => {
            return {
              ...item,
              session_id: null,
              data: {
                ...item.data,
                chatData: [],
                file_id: undefined,
                has_document: false,
                use_document: false,
              },
            };
          });

          makeToast('success', res?.data?.message);
          dispatch({
            type: 'SET_ACTIVE_SESSION',
            payload: null,
          });
          dispatch({
            type: 'TOGGLE_DATA_CHANGED',
          });
          dispatch({ type: 'SET_TODAY_SESSIONS', payload: [] });
          dispatch({ type: 'SET_LAST7DAYS_SESSIONS', payload: [] });
          dispatch({ type: 'SET_PINNED_SESSIONS', payload: [] });
          localStorage.setItem('chat', JSON.stringify(updatedData));
          dispatch({ type: 'SET_CHAT_HISTORY', payload: [] });
          setIsContentAvailable(false);
        }
      })
      .catch(() => {
        makeToast('error', 'Error Clearing Sessions');
      });
  };

  const hasMoreSessions =
    (state?.sessionHistory.offset ?? 0) + (state?.sessionHistory.limit ?? 0) <
    (state.totalSessions ?? 0);

  const filterData = (e: string) => {
    dispatch({ type: 'SET_TODAY_SESSIONS', payload: [] });
    dispatch({ type: 'SET_LAST7DAYS_SESSIONS', payload: [] });
    dispatch({ type: 'SET_PINNED_SESSIONS', payload: [] });
    dispatch({
      type: 'SET_SESSION_HISTORY',
      payload: {
        search: e,
        limit: 10,
        offset: 0,
      },
    });
    dispatch({
      type: 'SET_PIN_SESSION_HISTORY',
      payload: { search: e },
    });
  };

  const debouncedUpdateMaxToken = useCallback(
    debounce((e: string) => {
      filterData(e);
    }, 700),
    [],
  );

  // Calculate position: top should be header height (70px), left should be button's left edge
  const headerHeight = 0;
  const topPosition = headerHeight;

  return (
    <div
      ref={panelRef}
      className={`fixed text-[#ffffff] bg-[#151F26] shadow-lg p-4 pb-2 flex flex-col w-[28.5rem] transition-all duration-300 ease-in-out ${
        isOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
      }`}
      style={{
        top: `${topPosition}px`,
        right: `0px`,
        height: `100%`,
        zIndex: 10000,
      }}
    >
      <button className="absolute top-2 right-8 text-white" onClick={onClose} type="button">
        ✕
      </button>

      <button
        className="flex items-center justify-center my-2 gap-x-2 w-full rounded-md px-2 py-1 transition-transform duration-200 hover:scale-105 flex-shrink-0 bg-[#F48B00] disabled:opacity-50 disabled:cursor-not-allowed"
        onClick={handleNewSession}
        type="button"
        disabled={showLlmSetupBanner}
        title={
          showLlmSetupBanner
            ? 'Please add at least one provider or LLM model for your account to create a new chat'
            : ''
        }
      >
        <img src={PlusIcon} alt="Session History Icon" className="font-semibold" />
        <h2 className="text-lg font-semibold">{NewSessionText}</h2>
      </button>

      <div className="session-searchbar-wrapper flex-shrink-0">
        <SearchBar
          placeholder="Search"
          handleSearchByChange={(e) => debouncedUpdateMaxToken(e)}
          value={state?.sessionHistory?.search}
          className="tableSearch text-white bg-slate-300/10 "
          backgroundColor="bg-transparent"
          whiteSearchIcon
        />
      </div>

      <div className="mt-3 flex flex-col flex-1 min-h-0">
        <div className="flex-shrink-0">
          <div className="flex items-center gap-x-2">
            <img src={BluePin} alt="blue-pin" height={9} width={9} />
            <h3 className="text-lg font-semibold text-[#ffffff] mb-1 mr-2">{Pinnedtext}</h3>
          </div>
          {(() => {
            if (state.pinnedLoading) {
              return (
                <div className="text-gray-300">
                  <Loader width="10px" height="10px" />
                </div>
              );
            }
            if (state?.pinnedSessions?.length > 0) {
              return (
                <div className="overflow-y-auto max-h-44 invisible-scroller mb-2">
                  <ChatHistoryCard
                    sessions={state?.pinnedSessions}
                    sessionId={sessionId}
                    state={state}
                    dispatch={dispatch}
                    actionMenuRef={actionMenuRef}
                    inputSessionNameRef={inputSessionNameRef}
                    handleGetSession={handleSessionId}
                    handleNewSession={handleNewSession}
                  />
                </div>
              );
            }
            return <div className="text-gray-300 text-center mb-2">{NoSessionFoundText}</div>;
          })()}
        </div>
        <hr className="border-t border-[#0A8BC7] my-2 w-full flex-shrink-0" />
        <div id="scrollableDiv" className="flex-1 overflow-y-scroll invisible-scroller min-h-0">
          <InfiniteScroll
            dataLength={state.todaySessions.length + state.last7DaysSessions.length}
            next={() => {
              if (!state.sessionsLoading && hasMoreSessions) {
                dispatch({
                  type: 'SET_SESSION_HISTORY',
                  payload: {
                    ...state?.sessionHistory,
                    offset: state.sessionHistory.offset + state.sessionHistory.limit,
                  },
                });
              }
            }}
            hasMore={hasMoreSessions}
            loader={
              <div className="text-center text-sm text-gray-400 mt-2">
                <Loader width="10px" height="10px" />
              </div>
            }
            scrollableTarget="scrollableDiv"
          >
            <div className="flex items-center gap-x-2">
              <img src={Recents} alt="blue-pin" height={12} width={12} />
              <h3 className="text-lg font-semibold text-[#ffffff] mb-0">{TodayText}</h3>
            </div>
            {(() => {
              if (state.sessionsLoading && state.todaySessions.length === 0) {
                return (
                  <div className="text-gray-300">
                    <Loader width="10px" height="10px" />
                  </div>
                );
              }
              if (state.todaySessions.length > 0) {
                return (
                  <ChatHistoryCard
                    sessions={state?.todaySessions}
                    sessionId={sessionId}
                    state={state}
                    dispatch={dispatch}
                    actionMenuRef={actionMenuRef}
                    inputSessionNameRef={inputSessionNameRef}
                    handleGetSession={handleSessionId}
                    handleNewSession={handleNewSession}
                  />
                );
              }
              return <div className="text-gray-300 text-center">{NoSessionFoundText}</div>;
            })()}
            <hr className="border-t border-[#0A8BC7] my-2 w-full" />
            <div className="flex items-center gap-x-2">
              <img src={Recents} alt="blue-pin" height={12} width={12} />
              <h3 className="text-lg font-semibold text-[#ffffff] mt-2 mb-2">{LastDays}</h3>
            </div>
            <>
              {(() => {
                if (state.sessionsLoading && state.last7DaysSessions.length === 0) {
                  return (
                    <div className="text-gray-300">
                      <Loader width="10px" height="10px" />
                    </div>
                  );
                }
                if (state.last7DaysSessions.length > 0) {
                  return (
                    <ChatHistoryCard
                      sessions={state.last7DaysSessions}
                      sessionId={sessionId}
                      state={state}
                      dispatch={dispatch}
                      actionMenuRef={actionMenuRef}
                      inputSessionNameRef={inputSessionNameRef}
                      handleGetSession={handleSessionId}
                      handleNewSession={handleNewSession}
                    />
                  );
                }
                return <div className="text-gray-300 text-center">{NoSessionFoundText}</div>;
              })()}
            </>
          </InfiniteScroll>
        </div>
      </div>

      {/* Clear Sessions - Stuck to bottom */}
      <div className="flex items-center justify-center pt-2 pb-2 flex-shrink-0">
        <button
          className={`flex bg-[#151F26] text-lg w-full items-center justify-center font-medium text-white gap-x-2 border mx-4 px-2 py-1 rounded-md transition-transform duration-200 ${
            state?.pinnedSessions?.length === 0 &&
            state?.todaySessions?.length === 0 &&
            state?.last7DaysSessions?.length === 0
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:scale-105'
          }`}
          disabled={
            state?.pinnedSessions?.length === 0 &&
            state?.todaySessions?.length === 0 &&
            state?.last7DaysSessions?.length === 0
          }
          onClick={handleClearSessions}
          type="button"
        >
          <img src={WhiteTrash} className="mb-1" alt="white-trash" />
          <span>{ClearSessionText}</span>
        </button>
      </div>
    </div>
  );
}

export default ChatHistoryPanel;
